import * as React from 'react'

import { arrayMoveImmutable } from 'array-move'
import { EyeIcon, EyeOffIcon, GripVerticalIcon, RepeatIcon } from 'lucide-react'
import { action, computed, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'
import { SortableContainer, SortableElement } from 'react-sortable-hoc'

import { Button } from '~/client/src/shared/components/Button/Button'
import { ILWFCColumn } from '~/client/src/shared/components/ListWithFixedColumns/VirtualListHelpers'
import SearchBar from '~/client/src/shared/components/SearchBar/SearchBar'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { NOOP } from '~/client/src/shared/utils/noop'
import { areObjectArraysEqual } from '~/client/src/shared/utils/util'
import { UNASSIGNED } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

import {
  buildColumnsMenuSections,
  ColumnsConfigSection,
  moveColumnsMenuItem,
} from './columnsConfigurationMenuHelpers'

import './ColumnsConfigurationMenu.scss'

const none = 'None'
interface IProps {
  columns?: ILWFCColumn[]
  searchKey: string
  excludedFromSort?: string[]
  restoreDefaults: () => void
  updateColumnsConfiguration?: (collection: ILWFCColumn[]) => void
  changeSearchKey: (newSearchKey: string) => void
  isLoading?: boolean
  className?: string
}

const SortableItem = SortableElement(({ item }) => (
  <div
    className={classList({
      'sortable-item full': true,
      hidden: !item.props.children,
    })}
  >
    {item}
  </div>
))

const SortableList = SortableContainer(({ items, setHoveringSection }) => {
  const { hiddenItems, shownItems, freezedItems } = items
  return (
    <div
      className={classList({
        col: true,
      })}
    >
      {/* Freezed items */}
      <div
        onMouseEnter={() => setHoveringSection(ColumnsConfigSection.Freezed)}
      >
        {freezedItems.map((item, index) => (
          <SortableItem
            key={`freezed-item-${index}`}
            index={index}
            item={item}
          />
        ))}

        <div className="row section-separator pt10 px24 overflow-hidden">
          <hr className="ba-none bt-light-input-border min-width30" />
          <span className="text large light no-white-space-wrap lpMinus01 line-extra-large w-fit-content mx10 ellipsis flex-grow3">
            {Localization.translator.freezeColumns}
          </span>
          <hr className="ba-none bt-light-input-border min-width30" />
        </div>
      </div>

      {/* Shown items */}
      <div
        className="pt10"
        onMouseEnter={() => setHoveringSection(ColumnsConfigSection.None)}
      >
        {shownItems.map((item, index) => (
          <SortableItem
            key={`item-${index}`}
            index={freezedItems?.length + index}
            item={item}
          />
        ))}
      </div>

      {/* Hidden items */}
      {hiddenItems?.length > 0 && (
        <div
          onMouseEnter={() => setHoveringSection(ColumnsConfigSection.Hidden)}
        >
          <div className="row py10 px16">
            <div className="text large medium-bold lpMinus01 line-extra-large">
              {Localization.translator.hiddenInTable}
            </div>
          </div>
          {hiddenItems.map((item, index) => (
            <SortableItem
              key={`hidden-item-${index}`}
              index={freezedItems?.length + shownItems.length + index}
              item={item}
            />
          ))}
        </div>
      )}
    </div>
  )
})

@observer
export default class ColumnsConfigurationMenu extends React.Component<IProps> {
  @observable private hoveringSection: ColumnsConfigSection =
    ColumnsConfigSection.None
  @observable private localProps: IProps = null

  public constructor(props: IProps) {
    super(props)

    this.localProps = props
    makeObservable(this)
  }

  @action
  public componentDidUpdate(): void {
    if (!areObjectArraysEqual(this.localProps.columns, this.props.columns))
      this.localProps.columns = this.props.columns
    if (this.localProps.searchKey !== this.props.searchKey)
      this.localProps.searchKey = this.props.searchKey
    if (this.localProps.isLoading !== this.props.isLoading)
      this.localProps.isLoading = this.props.isLoading
  }

  public render() {
    const { searchKey, isLoading, className } = this.localProps
    const { restoreDefaults, changeSearchKey } = this.props

    return (
      <div
        className={classList({
          'col pt12 columns-config-menu fixed modal-shadow': true,
          [className]: !!className,
        })}
      >
        <SearchBar
          className="mx12 ba-light-grey"
          value={searchKey}
          onChange={changeSearchKey}
          onReset={() => changeSearchKey('')}
          onTagClick={NOOP}
          customPlaceholder={Localization.translator.searchProperties}
        />
        <div className="row py10 px12">
          <div className="text large medium-bold lpMinus01 line-extra-large pl16">
            {Localization.translator.shownInTable}
          </div>
          <div
            className={
              'row text large mr6 mw40 grey pointer no-outline-container'
            }
            onClick={restoreDefaults}
            title={Localization.translator.restoreDefaults}
          >
            <RepeatIcon size={16} className="no-grow mx12" />
          </div>
        </div>
        {isLoading ? this.renderLoader() : this.renderColumnsList()}
      </div>
    )
  }

  @action.bound
  private setHoveringSection(section: ColumnsConfigSection) {
    this.hoveringSection = section
  }

  private renderLoader(): JSX.Element {
    return (
      <div className="col pb12 px12 columns-list no-select">
        <div className="col x-center y-center full-height">
          <Spinner size="xs" />
          <div className="text medium pt12">
            {Localization.translator.loading}...
          </div>
        </div>
      </div>
    )
  }

  private renderColumnsList(): JSX.Element {
    return (
      <div className="col pb12 px12 columns-list no-select">
        <div className="col overflow-auto full-height sortable-list-holder">
          <SortableList
            items={this.items}
            onSortEnd={this.onSortEnd}
            setHoveringSection={this.setHoveringSection}
            axis="y"
            distance={2}
          />
        </div>
      </div>
    )
  }

  @computed
  private get items() {
    const { columns = [], searchKey, excludedFromSort = [] } = this.localProps

    const items = buildColumnsMenuSections({
      columns,
      searchKey,
      excludedFromSort,
    })

    return {
      freezedItems: this.renderItems(items.freezedItems),
      hiddenItems: this.renderItems(items.hiddenItems),
      shownItems: this.renderItems(items.shownItems),
    }
  }

  private renderItems(items: ILWFCColumn[]): JSX.Element[] {
    const { excludedFromSort = [] } = this.localProps
    return items.map(item => {
      return (
        <>
          {!excludedFromSort.includes(item.dataKey) && (
            <div key={item.dataKey || UNASSIGNED} className="row py10 pl16">
              <GripVerticalIcon
                size={16}
                className="dragging-trigger no-grow"
              />
              <div>
                {item && item.label ? (
                  <div className="text large ellipsis px8">{item.label}</div>
                ) : (
                  <div className="text large ellipsis px8">{none}</div>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon-sm"
                className="tw:flex-none! tw:mr-1.5 tw:text-muted-foreground"
                aria-label={
                  item.isHidden
                    ? Localization.translator.show_verb
                    : Localization.translator.hide
                }
                onClick={this.toggleItemVisibility.bind(null, item.dataKey)}
              >
                {item.isHidden ? <EyeOffIcon /> : <EyeIcon />}
              </Button>
            </div>
          )}
        </>
      )
    })
  }

  @action
  private onSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number
    newIndex: number
  }) => {
    const {
      columns = [],
      updateColumnsConfiguration,
      searchKey,
      excludedFromSort,
    } = this.localProps

    const collection = moveColumnsMenuItem({
      columns,
      searchKey,
      excludedFromSort,
      oldIndex,
      newIndex,
      section: this.hoveringSection,
    })

    updateColumnsConfiguration(collection)
  }

  private toggleItemVisibility = (dataKey: string) => {
    const { columns, updateColumnsConfiguration } = this.localProps

    let collection = columns.slice()

    let firstHiddenItemIndex = collection.map(_ => _.isHidden).indexOf(true)
    const itemCurrentIndex = collection.findIndex(_ => _.dataKey == dataKey)

    if (firstHiddenItemIndex === -1) {
      // There are no hidden elements in the collection
      // So, this adjustment will make itemCurrentIndex < firstHiddenItemIndex
      // to get itemNewIndex = last index of the collection during next code instruction
      firstHiddenItemIndex = collection.length
    }

    const itemNewIndex =
      itemCurrentIndex < firstHiddenItemIndex
        ? firstHiddenItemIndex - 1
        : firstHiddenItemIndex

    collection = arrayMoveImmutable(collection, itemCurrentIndex, itemNewIndex)

    collection[itemNewIndex] = {
      ...collection[itemNewIndex],
      isHidden: !collection[itemNewIndex].isHidden,
      isFixed: false,
    }
    updateColumnsConfiguration(collection)
  }
}
